import React, { useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Alert, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import Modal from '../../Utilities/DraggableModal';
import { useMsal } from '@azure/msal-react';
import useFetch from '../../Utilities/useFetch';
import FuncFetch from '../../Utilities/funcFetch';

const getPromptValue = (prompt, keys) => {
    for (const key of keys) {
        if (prompt?.[key] !== undefined && prompt?.[key] !== null && prompt?.[key] !== '') {
            return prompt[key];
        }
    }

    return null;
};

const emptyFormState = {
    aiPromptID: null,
    promptLabel: '',
    prompt: '',
    projectID: '',
    dataSource: '',
};

const buildFormState = (prompt) => ({
    aiPromptID: getPromptValue(prompt, ['aiPromptID', 'AIPromptID', 'aIPromptID', 'id', 'Id']),
    promptLabel: getPromptValue(prompt, ['promptLabel', 'PromptLabel', 'label', 'name']) ?? '',
    prompt: getPromptValue(prompt, ['prompt', 'PromptText', 'promptText', 'Prompt', 'body', 'template', 'content']) ?? '',
    projectID: getPromptValue(prompt, ['projectID', 'ProjectID', 'projectId', 'ProjectId']) ?? '',
    dataSource: getPromptValue(prompt, ['dataSource', 'DataSource', 'datasource', 'Source', 'source']) ?? '',
});

const buildPayload = (formData, isCreateMode) => ({
    aiPromptID: isCreateMode ? 0 : (formData.aiPromptID ?? null),
    promptLabel: formData.promptLabel.trim(),
    prompt: formData.prompt.trim(),
    projectID: formData.projectID?.trim() || null,
    dataSource: formData.dataSource?.trim() || null,
});

function AllAIPrompts() {
    const [rowData, setRowData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    const [formData, setFormData] = useState(emptyFormState);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const { instance } = useMsal();
    const { data: prompts, loading, refetch } = useFetch(true, `${process.env.REACT_APP_API_URL}/Claims/GetAIPrompts`, 'GET');

    useEffect(() => {
        if (Array.isArray(prompts)) {
            setRowData(prompts);
        }
    }, [prompts]);

    const openCreateModal = () => {
        setSelectedPrompt(null);
        setFormData(buildFormState(null));
        setError('');
        setShowModal(true);
    };

    const openEditModal = (prompt) => {
        setSelectedPrompt(prompt);
        setFormData(buildFormState(prompt));
        setError('');
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedPrompt(null);
        setFormData(emptyFormState);
        setError('');
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSave = async () => {
        if (!formData.promptLabel?.trim()) {
            setError('Prompt label is required.');
            return;
        }

        if (!formData.prompt?.trim()) {
            setError('Prompt is required.');
            return;
        }

        try {
            setSaving(true);
            setError('');

            const payload = buildPayload(formData, !selectedPrompt);

            const endpoint = '/Admin/PostAIPrompt';

            try {
                const response = await FuncFetch(
                    `${process.env.REACT_APP_API_URL}${endpoint}`,
                    null,
                    'POST',
                    payload,
                    instance,
                    null
                );

                if (response !== undefined) {
                    closeModal();
                    refetch();
                    return;
                }

                throw new Error('The AI prompt save request returned no response.');
            } catch (requestError) {
                console.error('AIPrompt save attempt failed', requestError);
                throw requestError;
            }
        } catch (saveError) {
            setError(saveError?.message || 'Failed to save AI prompt.');
        } finally {
            setSaving(false);
        }
    };

    const columnDefs = [
        {
            headerName: 'ID',
            field: 'aiPromptID',
            valueGetter: (params) => getPromptValue(params.data, ['aiPromptID', 'AIPromptID', 'aIPromptID', 'id', 'Id']),
            sortable: true,
            filter: true,
            width: 100,
        },
        {
            headerName: 'Prompt Label',
            field: 'promptLabel',
            valueGetter: (params) => getPromptValue(params.data, ['promptLabel', 'PromptLabel', 'label', 'name']),
            sortable: true,
            filter: true,
        },
        {
            headerName: 'Prompt',
            field: 'prompt',
            valueGetter: (params) => getPromptValue(params.data, ['prompt', 'PromptText', 'promptText', 'Prompt', 'body', 'template', 'content']),
            sortable: true,
            filter: true,
            minWidth: 320,
            cellStyle: { whiteSpace: 'normal' },
        },
        {
            headerName: 'ProjectID',
            field: 'projectID',
            valueGetter: (params) => getPromptValue(params.data, ['projectID', 'ProjectID', 'projectId', 'ProjectId']),
            sortable: true,
            filter: true,
            width: 130,
        },
        {
            headerName: 'DataSource',
            field: 'dataSource',
            valueGetter: (params) => getPromptValue(params.data, ['dataSource', 'DataSource', 'datasource', 'Source', 'source']),
            sortable: true,
            filter: true,
            width: 160,
        },
    ];

    return (
        <div>
            <Row style={{ marginTop: '10px' }}>
                <Col>
                    <h2>AI Prompts</h2>
                </Col>
                <Col>
                    {loading && <Spinner animation="border" />}
                </Col>
                <Col style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button onClick={openCreateModal}>Add Prompt</Button>
                </Col>
            </Row>

            {error && <Alert variant="danger">{error}</Alert>}

            <div className="ag-theme-quartz grid">
                <AgGridReact
                    rowData={rowData}
                    columnDefs={columnDefs}
                    onRowClicked={(event) => openEditModal(event.data)}
                    defaultColDef={{
                        resizable: true,
                        sortable: true,
                        filter: true,
                    }}
                    onFirstDataRendered={(params) => params.api.sizeColumnsToFit()}
                />
            </div>

            <Modal show={showModal} onHide={closeModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{selectedPrompt ? 'Edit AI Prompt' : 'Create AI Prompt'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Prompt Label</Form.Label>
                            <Form.Control
                                name="promptLabel"
                                value={formData.promptLabel}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Prompt</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={8}
                                name="prompt"
                                value={formData.prompt}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>ProjectID</Form.Label>
                            <Form.Control
                                name="projectID"
                                value={formData.projectID}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>DataSource</Form.Label>
                            <Form.Control
                                name="dataSource"
                                value={formData.dataSource}
                                onChange={handleChange}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner animation="border" size="sm" /> : selectedPrompt ? 'Save Changes' : 'Create Prompt'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default AllAIPrompts;
